import { useEffect, useState, useCallback } from "react";
import api, { apiError, ROLE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, UserCog } from "lucide-react";

const EMPTY = { name: "", email: "", password: "", role: "staff", phone: "" };

export default function Staff() {
  const { user, isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/staff"); setItems(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ name: s.name, email: s.email, password: "", role: s.role, phone: s.phone || "" }); setEditId(s.id); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name || !form.email) { toast.error("Ad ve e-posta zorunlu"); return; }
    if (!editId && !form.password) { toast.error("Şifre zorunlu"); return; }
    setSaving(true);
    try {
      if (editId) {
        const payload = { name: form.name, email: form.email, role: form.role, phone: form.phone };
        if (form.password) payload.password = form.password;
        await api.put(`/staff/${editId}`, payload);
      } else {
        await api.post("/staff", form);
      }
      toast.success(editId ? "Personel güncellendi" : "Personel eklendi");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/staff/${id}`); toast.success("Personel silindi"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <PageHeader title="Personel Yönetimi" subtitle="Sistem kullanıcılarını yönetin."
        action={isAdmin ? <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={openNew} data-testid="add-staff-btn"><Plus className="w-4 h-4 mr-2" />Yeni Personel</Button> : null} />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map((s) => (
            <Card key={s.id} className="p-5 border-slate-200 hover:shadow-md transition-all duration-200" data-testid={`staff-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-black text-white flex items-center justify-center font-bold">{s.name.charAt(0).toUpperCase()}</div>
                  <div>
                    <p className="font-heading font-bold">{s.name}</p>
                    <Badge className={s.role === "admin" ? "bg-rose-600 mt-1" : s.role === "manager" ? "bg-amber-500 mt-1" : "mt-1"} variant={s.role === "admin" || s.role === "manager" ? "default" : "secondary"}>{ROLE_LABEL[s.role] || "Personel"}</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  {isAdmin && (<>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                  {s.email !== user?.email && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" data-testid={`delete-staff-${s.id}`}><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Personeli sil?</AlertDialogTitle><AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Vazgeç</AlertDialogCancel><AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => remove(s.id)}>Sil</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  </>)}
                </div>
              </div>
              <div className="mt-3 text-sm text-gray-600">
                <p className="truncate">{s.email}</p>
                {s.phone && <p>{s.phone}</p>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Personeli Düzenle" : "Yeni Personel"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Ad Soyad *</Label><Input value={form.name} onChange={set("name")} data-testid="sf-name" /></div>
            <div className="space-y-2"><Label>E-posta *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="sf-email" /></div>
            <div className="space-y-2"><Label>Şifre {editId && "(değiştirmek için doldurun)"}</Label><Input type="password" value={form.password} onChange={set("password")} data-testid="sf-password" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Telefon</Label><Input value={form.phone} onChange={set("phone")} data-testid="sf-phone" /></div>
              <div className="space-y-2"><Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="sf-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="staff">Personel</SelectItem><SelectItem value="manager">Müdür</SelectItem><SelectItem value="admin">Yönetici</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={save} disabled={saving} data-testid="save-staff-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
